import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Tab,
  Tabs,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Alert,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import {
  AdminPanelSettings,
  CheckCircle,
  Cancel,
  Block,
  LockOpen,
  Person,
  AccountBalance,
  CreditCard,
  RequestQuote,
  Visibility,
  Schedule,
  Security
} from '@mui/icons-material';
import { useBanking } from '../context/BankingContext';
import { useAuth } from '../context/AuthContext';
import { formatCurrency, formatDate } from '../utils/mockData';
import { LoanApplication, Account, CreditCard as CreditCardType, User } from '../types';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const Admin: React.FC = () => {
  const { user } = useAuth();
  const {
    loanApplications,
    accounts,
    creditCards,
    allUsers,
    approveLoan,
    declineLoan,
    blockAccount,
    unblockAccount,
    blockCreditCard,
    unblockCreditCard
  } = useBanking();

  const [tabValue, setTabValue] = useState(0);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userDetailOpen, setUserDetailOpen] = useState(false);
  const [actionDialog, setActionDialog] = useState<{
    open: boolean;
    type: 'block-account' | 'unblock-account' | 'block-card' | 'unblock-card' | null;
    itemId: string;
    itemName: string;
  }>({
    open: false,
    type: null,
    itemId: '',
    itemName: ''
  });

  // Check if user is admin
  if (!user || user.role !== 'admin') {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          Access denied. Administrator privileges required.
        </Alert>
      </Container>
    );
  }

  // Statistics
  const pendingLoans = loanApplications.filter(app => app.status === 'pending');
  const blockedAccounts = accounts.filter(acc => acc.status === 'blocked');
  const blockedCards = creditCards.filter(card => card.status === 'blocked');
  const totalUsers = allUsers.filter(u => u.role === 'customer').length;

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleApproveLoan = async (applicationId: string) => {
    try {
      await approveLoan(applicationId);
    } catch (error) {
      console.error('Failed to approve loan:', error);
    }
  };

  const handleDeclineLoan = async (applicationId: string) => {
    try {
      await declineLoan(applicationId);
    } catch (error) {
      console.error('Failed to decline loan:', error);
    }
  };

  const handleAccountAction = async () => {
    if (!actionDialog.type || !actionDialog.itemId) return;

    try {
      if (actionDialog.type === 'block-account') {
        await blockAccount(actionDialog.itemId);
      } else if (actionDialog.type === 'unblock-account') {
        await unblockAccount(actionDialog.itemId);
      } else if (actionDialog.type === 'block-card') {
        await blockCreditCard(actionDialog.itemId);
      } else if (actionDialog.type === 'unblock-card') {
        await unblockCreditCard(actionDialog.itemId);
      }
      setActionDialog({ open: false, type: null, itemId: '', itemName: '' });
    } catch (error) {
      console.error('Failed to perform action:', error);
    }
  };

  const openActionDialog = (type: any, itemId: string, itemName: string) => {
    setActionDialog({ open: true, type, itemId, itemName });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'blocked': return 'error';
      case 'pending': return 'warning';
      case 'approved': return 'success';
      case 'declined': return 'error';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active': return 'Active';
      case 'blocked': return 'Blocked';
      case 'pending': return 'Pending';
      case 'approved': return 'Approved';
      case 'declined': return 'Declined';
      default: return status;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={4}>
        <AdminPanelSettings sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
        <Box>
          <Typography variant="h4" component="h1" fontWeight="bold">
            Administrator Dashboard
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Manage users, accounts, loans, and system settings
          </Typography>
        </Box>
      </Box>

      {/* Admin Statistics */}
      <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap' }}>
        <Card sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <RequestQuote sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
            <Typography variant="h5" fontWeight="bold">
              {pendingLoans.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Pending Loan Requests
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <Block sx={{ fontSize: 40, color: 'error.main', mb: 1 }} />
            <Typography variant="h5" fontWeight="bold">
              {blockedAccounts.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Blocked Accounts
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <Security sx={{ fontSize: 40, color: 'error.main', mb: 1 }} />
            <Typography variant="h5" fontWeight="bold">
              {blockedCards.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Blocked Cards
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <Person sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
            <Typography variant="h5" fontWeight="bold">
              {totalUsers}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Customers
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Admin Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="admin tabs">
            <Tab icon={<RequestQuote />} label="Loan Requests" />
            <Tab icon={<AccountBalance />} label="Accounts Management" />
            <Tab icon={<CreditCard />} label="Cards Management" />
            <Tab icon={<Person />} label="User Management" />
          </Tabs>
        </Box>

        {/* Loan Requests Tab */}
        <TabPanel value={tabValue} index={0}>
          <Typography variant="h6" gutterBottom>
            Pending Loan Applications
          </Typography>
          {pendingLoans.length === 0 ? (
            <Typography color="text.secondary">No pending loan applications</Typography>
          ) : (
            <List>
              {pendingLoans.map((application: LoanApplication) => {
                const applicant = allUsers.find(u => u.id === application.userId);
                return (
                  <React.Fragment key={application.id}>
                    <ListItem>
                      <Box sx={{ mr: 2 }}>
                        <Schedule color="warning" />
                      </Box>
                      <ListItemText
                        primary={
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="subtitle1" fontWeight="bold">
                              {application.loanType === 'auto' ? 'Auto Loan' : 'Personal Loan'} - {formatCurrency(application.amount)}
                            </Typography>
                            <Chip
                              label={application.status.toUpperCase()}
                              color={getStatusColor(application.status) as any}
                              size="small"
                            />
                          </Box>
                        }
                        secondary={
                          <Box mt={1}>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Applicant:</strong> {applicant?.fullName || 'Unknown'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Purpose:</strong> {application.purpose}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Applied:</strong> {formatDate(application.appliedAt)}
                            </Typography>
                          </Box>
                        }
                        secondaryTypographyProps={{ component: 'div' }}
                      />
                      <ListItemSecondaryAction>
                        <Box display="flex" gap={1}>
                          <Button
                            variant="contained"
                            color="success"
                            size="small"
                            startIcon={<CheckCircle />}
                            onClick={() => handleApproveLoan(application.id)}
                          >
                            Approve
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            startIcon={<Cancel />}
                            onClick={() => handleDeclineLoan(application.id)}
                          >
                            Decline
                          </Button>
                        </Box>
                      </ListItemSecondaryAction>
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </TabPanel>

        {/* Accounts Management Tab */}
        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" gutterBottom>
            All Customer Accounts
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Account Number</TableCell>
                  <TableCell>Owner</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Balance</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {accounts.map((account: Account) => {
                  const owner = allUsers.find(u => u.id === account.userId);
                  return (
                    <TableRow key={account.id}>
                      <TableCell>{account.accountNumber}</TableCell>
                      <TableCell>{owner?.fullName || 'Unknown'}</TableCell>
                      <TableCell sx={{ textTransform: 'capitalize' }}>{account.accountType}</TableCell>
                      <TableCell>{formatCurrency(account.balance)}</TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusText(account.status)}
                          color={getStatusColor(account.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {account.status === 'active' ? (
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            startIcon={<Block />}
                            onClick={() => openActionDialog('block-account', account.id, account.accountNumber)}
                          >
                            Block
                          </Button>
                        ) : (
                          <Button
                            variant="outlined"
                            color="success"
                            size="small"
                            startIcon={<LockOpen />}
                            onClick={() => openActionDialog('unblock-account', account.id, account.accountNumber)}
                          >
                            Unblock
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Cards Management Tab */}
        <TabPanel value={tabValue} index={2}>
          <Typography variant="h6" gutterBottom>
            All Credit Cards
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Card Number</TableCell>
                  <TableCell>Owner</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Credit Limit</TableCell>
                  <TableCell>Balance</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {creditCards.map((card: CreditCardType) => {
                  const owner = allUsers.find(u => u.id === card.userId);
                  return (
                    <TableRow key={card.id}>
                      <TableCell>{card.cardNumber}</TableCell>
                      <TableCell>{owner?.fullName || 'Unknown'}</TableCell>
                      <TableCell sx={{ textTransform: 'capitalize' }}>{card.cardType}</TableCell>
                      <TableCell>{formatCurrency(card.creditLimit)}</TableCell>
                      <TableCell>{formatCurrency(card.currentBalance)}</TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusText(card.status)}
                          color={getStatusColor(card.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {card.status === 'active' ? (
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            startIcon={<Block />}
                            onClick={() => openActionDialog('block-card', card.id, card.cardNumber)}
                          >
                            Block
                          </Button>
                        ) : (
                          <Button
                            variant="outlined"
                            color="success"
                            size="small"
                            startIcon={<LockOpen />}
                            onClick={() => openActionDialog('unblock-card', card.id, card.cardNumber)}
                          >
                            Unblock
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* User Management Tab */}
        <TabPanel value={tabValue} index={3}>
          <Typography variant="h6" gutterBottom>
            All Customers
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Mobile</TableCell>
                  <TableCell>Accounts</TableCell>
                  <TableCell>Credit Cards</TableCell>
                  <TableCell>Joined</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allUsers.filter(u => u.role === 'customer').map((customer: User) => {
                  const userAccounts = accounts.filter(acc => acc.userId === customer.id);
                  const userCards = creditCards.filter(card => card.userId === customer.id);
                  
                  return (
                    <TableRow key={customer.id}>
                      <TableCell>{customer.fullName}</TableCell>
                      <TableCell>{customer.email}</TableCell>
                      <TableCell>{customer.mobileNumber}</TableCell>
                      <TableCell>{userAccounts.length}</TableCell>
                      <TableCell>{userCards.length}</TableCell>
                      <TableCell>{formatDate(customer.createdAt)}</TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedUser(customer);
                            setUserDetailOpen(true);
                          }}
                        >
                          <Visibility />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
      </Card>

      {/* Action Confirmation Dialog */}
      <Dialog 
        open={actionDialog.open} 
        onClose={() => setActionDialog({ open: false, type: null, itemId: '', itemName: '' })}
        disableRestoreFocus
      >
        <DialogTitle>
          Confirm Action
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to {actionDialog.type?.includes('block') ? 'block' : 'unblock'} {actionDialog.itemName}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialog({ open: false, type: null, itemId: '', itemName: '' })}>
            Cancel
          </Button>
          <Button 
            onClick={handleAccountAction} 
            color={actionDialog.type?.includes('block') ? 'error' : 'success'}
            variant="contained"
          >
            {actionDialog.type?.includes('block') ? 'Block' : 'Unblock'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Detail Dialog */}
      <Dialog 
        open={userDetailOpen} 
        onClose={() => setUserDetailOpen(false)} 
        maxWidth="md" 
        fullWidth
        disableRestoreFocus
      >
        <DialogTitle>
          Customer Details: {selectedUser?.fullName}
        </DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box>
              <Typography variant="h6" gutterBottom>Personal Information</Typography>
              <Box sx={{ mb: 3 }}>
                <Typography><strong>Email:</strong> {selectedUser.email}</Typography>
                <Typography><strong>Mobile:</strong> {selectedUser.mobileNumber}</Typography>
                <Typography><strong>Address:</strong> {selectedUser.address.street}, {selectedUser.address.city}, {selectedUser.address.state} {selectedUser.address.zipCode}</Typography>
                <Typography><strong>Member Since:</strong> {formatDate(selectedUser.createdAt)}</Typography>
              </Box>
              
              <Typography variant="h6" gutterBottom>Account Summary</Typography>
              <Box sx={{ mb: 2 }}>
                <Typography>Accounts: {accounts.filter(acc => acc.userId === selectedUser.id).length}</Typography>
                <Typography>Credit Cards: {creditCards.filter(card => card.userId === selectedUser.id).length}</Typography>
                <Typography>Loans: {loanApplications.filter(app => app.userId === selectedUser.id).length}</Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Admin; 